<template>
  <div class="logoWrapper">
    <a v-if="linkUrl !== ''" :href="linkUrl" data-testid="logoAnchor">
      <img
        :src="logoSource"
        :alt="altText"
        :class="logoClass"
        data-testid="logo"
      />
    </a>
    <img
      v-else
      :src="logoSource"
      :alt="altText"
      :class="logoClass"
      data-testid="logo"
    />
  </div>
</template>

<script>
import * as logos from "../utils/bynderLogoUrls";
export default {
  name: "NsLogo",
  props: {
    /**
     * Notes for NsLogo usage:
     * Heirachy of prop usage goes from top to bottom (i.e. if first one is present, use that, etc)
     * See computed prop LogoSource() on how the image source is determined
     * See computed prop LogoClass() on how the image styles are determined
     */

    /**
     * is-market-logo-override --> if present, use marketLogoDesktop and marketLogoMobile Url string props with default class styles
     */
    isMarketLogoOverride: {
      type: Boolean,
      default: false
    },
    /**
     * market-logo-desktop-url --> if present, used in combination with isMarketLogoOverride === true in desktop screen width using default style/class
     */
    marketLogoDesktopUrl: {
      type: String,
      default: ""
    },
    /**
     * market-logo-mobile-url --> if present, used in combination with isMarketLogoOverride === true in mobile screen width using default style/class
     */
    marketLogoMobileUrl: {
      type: String,
      default: ""
    },
    /**
     * is-mysite --> if present, use mysiteImageUrl if present OR pick either black or white MARK logo as determined by isWhite prop
     */
    isMysite: {
      type: Boolean,
      default: false
    },
    /**
     * mysite-image-url --> if present, use mysiteImageUrl
     */
    mysiteImageUrl: {
      type: String,
      default: ""
    },
    /**
     * is-white --> determine if black or white logos will be used
     */
    isWhite: {
      type: Boolean,
      default: false
    },
    /**
     * is-powered-by --> if present, use the is-powered by logo (as long as no other props override its use)
     */
    isPoweredBy: {
      type: Boolean,
      default: false
    },
    /**
     * is-word-mark --> if present, use the is-work-mark logo (as long as no other props override its use)
     */
    isWordMark: {
      type: Boolean,
      default: false
    },
    /**
     * is-mark --> if present, use the is-mark logo (as long as no other props override its use)
     */
    isMark: {
      type: Boolean,
      default: false
    },
    /**
     * is-horizontal --> overrides default styles in favor of horizontal logos in all screen widths
     */
    isHorizontal: {
      type: Boolean,
      default: false
    },
    /**
     * is-stacked --> overrides default styles in favor of square/stacked/uniform logos in all screen widths
     */
    isStacked: {
      type: Boolean,
      default: false
    },
    /**
     * link-url --> to NOT wrap the image/logo in an anchor tag, set this prop to an empty string i.e. ""
     */
    linkUrl: {
      type: String,
      default: "/"
    },
    /**
     * alt-text --> to NOT use default alt text, update this props argument when defining this component
     */
    altText: {
      type: String,
      default: "Nu Skin Logo"
    },
    /**
     * for-testing-purposes-only-is-desktop --> defining tests in desktop mode with computed properties with different screen widths
     */
    forTestingPurposesIsDesktop: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    desktop() {
      if (this.forTestingPurposesIsDesktop) {
        return true;
      } else return this.$mq === "desktop" || this.$mq === "desktopHD";
    },
    logoClass() {
      if (
        (this.isHorizontal || this.isWordMark || this.isPoweredBy) &&
        !this.mysiteImageUrl &&
        !this.isMarketLogoOverride
      ) {
        return "rectangularLogosBeingUsed";
      } else if (
        (this.isStacked || this.isMark || this.mysiteImageUrl) &&
        !this.isMarketLogoOverride
      ) {
        return "evenlyShapedLogosBeingUsed";
      } else return "default";
    },
    logoSource() {
      if (
        this.isMarketLogoOverride &&
        this.marketLogoDesktopUrl &&
        this.marketLogoMobileUrl
      ) {
        if (this.desktop || this.forTestingPurposesIsDesktop) {
          return this.marketLogoDesktopUrl;
        } else return this.marketLogoMobileUrl;
      } else if (this.mysiteImageUrl || this.isMysite) {
        // mysite choices
        if (this.mysiteImageUrl) {
          return this.mysiteImageUrl;
        }
        if (this.isWhite) {
          return logos.WHITE_MARK;
        } else return logos.DARK_MARK;
      } else if (!this.isWhite) {
        // dark nuskin logo choices
        if (this.isWordMark) {
          return logos.DARK_WORD_MARK;
        } else if (this.isMark) {
          return logos.DARK_MARK;
        } else if (this.isHorizontal) {
          return logos.DARK_HORIZONTAL;
        } else if (this.isStacked) {
          return logos.DARK_STACKED;
        } else if (this.isPoweredBy) {
          return logos.DARK_POWERED_BY;
        }
        if (this.desktop || this.forTestingPurposesIsDesktop) {
          return logos.DARK_STACKED;
        } else return logos.DARK_HORIZONTAL;
      } else {
        // white nuskin logo choices
        if (this.isWordMark) {
          return logos.WHITE_WORD_MARK;
        } else if (this.isMark) {
          return logos.WHITE_MARK;
        } else if (this.isHorizontal) {
          return logos.WHITE_HORIZONTAL;
        } else if (this.isStacked) {
          return logos.WHITE_STACKED;
        } else if (this.isPoweredBy) {
          return logos.WHITE_POWERED_BY;
        }
        if (this.desktop || this.forTestingPurposesIsDesktop) {
          return logos.WHITE_STACKED;
        } else return logos.WHITE_HORIZONTAL;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.logoWrapper {
  display: flex;
  justify-content: center;
}
.default {
  max-width: 104px;
  @media (min-width: 767px) {
    max-width: 132px;
  }
  @media (min-width: 1023px) {
    max-width: 60px;
  }
}
.rectangularLogosBeingUsed {
  max-width: 104px;
  @media (min-width: 1023px) {
    max-width: 132px;
  }
}
.evenlyShapedLogosBeingUsed {
  max-width: 60px;
}
</style>
